import { ConfirmDestructiveDialog } from "@/components/confirm-destructive-dialog";
import { CopyId } from "@/components/copy-id";
import { PermissionsEditor } from "@/components/permissions-editor";
import { SiteHeader } from "@/components/site-header";
import { UserPicker } from "@/components/user-picker";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useCanWriteHere } from "@/contexts/area.context";
import { useRefreshPermissions } from "@/hooks/use-permissions";
import {
  AREA_LABELS,
  AREAS,
  draftFromGrants,
  draftToGrants,
  resolveGrants,
  type Area,
  type GrantDraft,
  type Level,
} from "@/lib/permissions";
import { queryKeys } from "@/lib/queryKeys";
import {
  type SpecialUser,
  specialUsersProvider,
  type UpsertSpecialUserPayload,
} from "@/providers/special-users.provider";
import type { User } from "@/providers/users.provider";
import { useAuthStore } from "@/stores/auth.store";
import {
  IconAlertTriangle,
  IconBolt,
  IconEdit,
  IconLock,
  IconPlus,
  IconSearch,
  IconShieldLock,
  IconTrash,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";

// ── Types ─────────────────────────────────────────────────────────────────────

type SheetMode =
  | { type: "closed" }
  | { type: "create" }
  | { type: "edit"; grant: SpecialUser };

// ── Grant form sheet ──────────────────────────────────────────────────────────

function GrantFormSheet({
  mode,
  onClose,
}: {
  mode: Extract<SheetMode, { type: "create" | "edit" }>;
  onClose: () => void;
}) {
  const isEdit = mode.type === "edit";
  const existing = isEdit ? mode.grant : null;

  const currentUserId = useAuthStore((s) => s.user?.userId);
  const queryClient = useQueryClient();
  const refreshPermissions = useRefreshPermissions();

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [email, setEmail] = useState(existing?.email ?? "");
  const [note, setNote] = useState(existing?.note ?? "");
  const [bypass, setBypass] = useState(existing?.ratelimit_bypass ?? false);
  const [confirmSelfDemote, setConfirmSelfDemote] = useState(false);

  // A new grant starts from the template — every area explicitly at "none".
  // An explicit "none" beats the wildcard, so those rows have to be cleared
  // rather than layered under an "admin/*:write".
  const { data: template, isLoading: loadingTemplate } = useQuery({
    queryKey: queryKeys.permissionsTemplate(),
    queryFn: () => specialUsersProvider.getTemplate(),
    staleTime: Infinity,
    enabled: !isEdit,
  });

  const [draftOverride, setDraftOverride] = useState<GrantDraft | null>(null);
  const baseDraft = useMemo(
    () => draftFromGrants(existing?.permissions ?? template?.permissions ?? []),
    [existing, template],
  );
  const draft = draftOverride ?? baseDraft;

  const unknownEntries = useMemo(
    () => resolveGrants(existing?.permissions ?? []).unknown,
    [existing],
  );

  const userId = existing?.user_id ?? selectedUser?.user_id ?? "";
  const isSelf = !!currentUserId && userId === currentUserId;

  const before = resolveGrants(existing?.permissions ?? []).effective;
  const after = resolveGrants(draftToGrants(draft)).effective;
  const losesOwnAccess =
    isSelf && AREAS.some((a) => rank(after[a]) < rank(before[a]));

  const upsertMutation = useMutation({
    mutationFn: (payload: UpsertSpecialUserPayload) =>
      specialUsersProvider.upsert(userId, payload),
    onSuccess: (grant) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.specialUsers() });
      queryClient.setQueryData(queryKeys.specialUser(grant.user_id), grant);
      // Permissions apply immediately; ratelimit_bypass is cached for a minute.
      toast.success(
        grant.ratelimit_bypass !== (existing?.ratelimit_bypass ?? false)
          ? "Access updated — rate limit exemption takes effect within a minute."
          : "Access updated.",
      );
      // Editing your own grant changes what this session may render.
      if (isSelf) refreshPermissions();
      onClose();
    },
    onError: () => toast.error("Could not save the grant. Please try again."),
  });

  function buildPayload(): UpsertSpecialUserPayload {
    // Full replace: the entire array goes every time, never a delta.
    return {
      permissions: draftToGrants(draft),
      ratelimit_bypass: bypass,
      email: email.trim() || undefined,
      note: note.trim(),
    };
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!userId) {
      toast.error("Pick a user first.");
      return;
    }
    if (!note.trim()) {
      toast.error("Add a note explaining why this grant exists.");
      return;
    }
    if (losesOwnAccess && !confirmSelfDemote) {
      setConfirmSelfDemote(true);
      return;
    }

    upsertMutation.mutate(buildPayload());
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="flex h-full flex-col">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit grant" : "New grant"}</SheetTitle>
          <SheetDescription>
            Privileges follow the user ID — the email is a display label only.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-5 overflow-y-auto px-4">
          <div className="space-y-2">
            <Label>User</Label>
            {isEdit ? (
              <div className="rounded-lg border px-3 py-2">
                <p className="text-sm font-medium">
                  {existing?.email || "Unknown user"}
                </p>
                <p className="font-mono text-xs break-all text-muted-foreground">
                  {existing?.user_id}
                </p>
              </div>
            ) : (
              // Picked from a search: the API never checks that a pasted id is
              // a real account.
              <UserPicker
                selected={selectedUser}
                onSelect={(u) => {
                  setSelectedUser(u);
                  if (u) setEmail((prev) => prev || u.username);
                }}
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="grant-email">Email (label)</Label>
            <Input
              id="grant-email"
              type="text"
              placeholder="someone@remap.club"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Shown in the table so a human can tell whose grant this is.
              Changing it never moves the privilege.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="grant-note">Note</Label>
            <Textarea
              id="grant-note"
              placeholder="Why does this grant exist?"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Permissions</Label>
            {loadingTemplate ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <PermissionsEditor
                draft={draft}
                onChange={setDraftOverride}
                unknownEntries={unknownEntries}
              />
            )}
          </div>

          <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
            <div className="min-w-0 space-y-0.5">
              <Label htmlFor="grant-bypass" className="flex items-center gap-1.5">
                <IconBolt className="size-4" />
                Rate limit bypass
              </Label>
              <p className="text-xs text-muted-foreground">
                Not a permission. Skips rate limiting on the routes that opt in
                — not on login, password reset, or OTP. Propagates within a
                minute.
              </p>
            </div>
            <Switch
              id="grant-bypass"
              checked={bypass}
              onCheckedChange={setBypass}
            />
          </div>
        </div>

        <SheetFooter>
          <Button type="submit" disabled={upsertMutation.isPending}>
            {upsertMutation.isPending ? "Saving…" : "Save"}
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </SheetFooter>
      </form>

      <AlertDialog
        open={confirmSelfDemote}
        onOpenChange={(open) => {
          if (!open) setConfirmSelfDemote(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reduce your own access?</AlertDialogTitle>
            <AlertDialogDescription>
              This is your own grant, and permission changes take effect on your
              very next request. You may lose access to this screen and need
              another admin to restore it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmSelfDemote(false);
                upsertMutation.mutate(buildPayload());
              }}
            >
              Save anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

const rank = (level: Level) => (level === "write" ? 2 : level === "read" ? 1 : 0);

// ── Table row ─────────────────────────────────────────────────────────────────

function PermissionSummary({ grant }: { grant: SpecialUser }) {
  const { effective, unknown } = resolveGrants(grant.permissions);
  const adminAreas = AREAS.filter((a) => a !== "docs");
  const granted = AREAS.filter((a) => effective[a] !== "none");

  if (unknown.length) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
        <IconAlertTriangle className="size-3" />
        Invalid — no access
      </span>
    );
  }

  if (!granted.length) {
    return (
      <span className="text-xs italic text-muted-foreground opacity-60">
        none
      </span>
    );
  }

  if (adminAreas.every((a) => effective[a] === "write")) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
        <IconShieldLock className="size-3" />
        Full admin
      </span>
    );
  }

  const shown = granted.slice(0, 3);
  return (
    <>
      {shown.map((area) => (
        <AreaChip key={area} area={area} level={effective[area]} />
      ))}
      {granted.length > shown.length && (
        <span className="text-xs text-muted-foreground">
          +{granted.length - shown.length}
        </span>
      )}
    </>
  );
}

function AreaChip({ area, level }: { area: Area; level: Level }) {
  return (
    <span
      className={
        level === "write"
          ? "inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary"
          : "inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
      }
    >
      {AREA_LABELS[area]}
      <span className="opacity-60">{level}</span>
    </span>
  );
}

function GrantRow({
  grant,
  isSelf,
  canWrite,
  currentUserId,
  onEdit,
  revokeMutation,
}: {
  grant: SpecialUser;
  isSelf: boolean;
  canWrite: boolean;
  currentUserId: string | undefined;
  onEdit: (grant: SpecialUser) => void;
  revokeMutation: ReturnType<typeof useMutation<void, Error, string>>;
}) {
  return (
    <TableRow>
      <TableCell>
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium">
            {grant.email || <span className="italic opacity-40">no email</span>}
            {isSelf && (
              <span className="ml-2 rounded-full bg-muted px-1.5 py-px text-[10px] uppercase tracking-wide text-muted-foreground">
                you
              </span>
            )}
          </span>
          <CopyId id={grant.user_id} />
        </div>
      </TableCell>
      <TableCell>
        <div className="flex max-w-xs flex-wrap items-center gap-1">
          <PermissionSummary grant={grant} />
          {grant.ratelimit_bypass && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-400">
              <IconBolt className="size-3" />
              Bypass
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className="max-w-[280px] truncate text-muted-foreground">
        {grant.note || <span className="italic opacity-40">—</span>}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {new Date(grant.created_at).toLocaleDateString()}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {new Date(grant.updated_at).toLocaleString()}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="size-9"
            title={canWrite ? "Edit grant" : "Read-only: needs write on Special Users"}
            disabled={!canWrite}
            onClick={() => onEdit(grant)}
          >
            <IconEdit className="size-4" />
          </Button>
          <ConfirmDestructiveDialog
            confirmValue={grant.user_id}
            confirmLabel="user ID"
            title={`Revoke all privileges for ${grant.email || grant.user_id}?`}
            description={
              <>
                This removes every permission and the rate limit exemption.
                {grant.user_id === currentUserId &&
                  " This is your own account — you will lose access on your next request."}{" "}
                To drop only some areas, edit the grant instead.
              </>
            }
            actionLabel="Revoke"
            onConfirm={() => revokeMutation.mutate(grant.user_id)}
            isPending={
              revokeMutation.isPending &&
              revokeMutation.variables === grant.user_id
            }
            trigger={({ disabled }) => (
              <Button
                variant="ghost"
                size="icon"
                className="size-9 text-destructive hover:text-destructive"
                title={
                  canWrite
                    ? "Revoke all privileges"
                    : "Read-only: needs write on Special Users"
                }
                disabled={!canWrite || disabled}
              >
                <IconTrash className="size-4" />
              </Button>
            )}
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function SpecialUsersPage() {
  const [sheet, setSheet] = useState<SheetMode>({ type: "closed" });
  const [filter, setFilter] = useState("");

  const currentUserId = useAuthStore((s) => s.user?.userId);
  const canWrite = useCanWriteHere();
  const queryClient = useQueryClient();
  const refreshPermissions = useRefreshPermissions();

  const {
    data: grants,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.specialUsers(),
    queryFn: () => specialUsersProvider.list(),
  });

  const revokeMutation = useMutation({
    mutationFn: (userId: string) => specialUsersProvider.revoke(userId),
    onSuccess: (_, userId) => {
      queryClient.setQueryData<SpecialUser[]>(
        queryKeys.specialUsers(),
        (prev) => prev?.filter((g) => g.user_id !== userId) ?? [],
      );
      queryClient.removeQueries({ queryKey: queryKeys.specialUser(userId) });
      if (userId === currentUserId) refreshPermissions();
      toast.success("Privileges revoked.");
    },
    onError: () => toast.error("Could not revoke the grant. Please try again."),
  });

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return grants ?? [];
    return (grants ?? []).filter(
      (g) =>
        g.email?.toLowerCase().includes(q) ||
        g.note?.toLowerCase().includes(q) ||
        g.user_id.toLowerCase().includes(q) ||
        g.permissions.some((p) => p.toLowerCase().includes(q)),
    );
  }, [grants, filter]);

  const isSheetOpen = sheet.type !== "closed";

  return (
    <div className="flex h-full flex-col">
      <SiteHeader title="Special Users" />

      <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 pb-2 lg:px-6 lg:pt-6">
        <div className="relative w-full max-w-sm">
          <IconSearch className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Filter by email, note, id or permission…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        {canWrite ? (
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => setSheet({ type: "create" })}
          >
            <IconPlus className="size-4" />
            New grant
          </Button>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs text-muted-foreground">
            <IconLock className="size-3.5" />
            Read-only
          </span>
        )}
      </div>

      <div className="flex-1 overflow-auto px-4 pb-4 lg:px-6 lg:pb-6">
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead>Note</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="py-12 text-center text-muted-foreground"
                  >
                    Failed to load grants. Check your connection and try again.
                  </TableCell>
                </TableRow>
              ) : !filtered.length ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="py-12 text-center text-muted-foreground"
                  >
                    {grants?.length
                      ? "No grants match this filter."
                      : "No special users yet."}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((grant) => (
                  <GrantRow
                    key={grant.user_id}
                    grant={grant}
                    isSelf={grant.user_id === currentUserId}
                    canWrite={canWrite}
                    currentUserId={currentUserId}
                    onEdit={(g) => setSheet({ type: "edit", grant: g })}
                    revokeMutation={revokeMutation}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Sheet
        open={isSheetOpen}
        onOpenChange={(open) => {
          if (!open) setSheet({ type: "closed" });
        }}
      >
        <SheetContent side="right" className="w-full sm:max-w-lg">
          {(sheet.type === "create" || sheet.type === "edit") && (
            <GrantFormSheet
              key={sheet.type === "edit" ? sheet.grant.user_id : "create"}
              mode={sheet}
              onClose={() => setSheet({ type: "closed" })}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default SpecialUsersPage;
